import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { allowanceService } from '../../services/allowance.service';
import { tripService } from '../../services/trip.service';
import { AllowanceType, CreateAllowanceRequest } from '../../types';

interface CalculateAllowanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const schema = z.object({
  tripId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'Trip is required')
  ),
  type: z.string().min(1, 'Type is required'),
  amount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Amount must be positive')
  ),
  notes: z.string().optional(),
});

type FormInput = z.infer<typeof schema>;

export const CalculateAllowanceModal: React.FC<CalculateAllowanceModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();

  const { data: trips, isLoading } = useQuery({
    queryKey: ['trips'],
    queryFn: () => tripService.getAll(),
    enabled: isOpen,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!isOpen) {
      reset({});
    }
  }, [isOpen, reset]);

  const mutation = useMutation({
    mutationFn: (data: CreateAllowanceRequest) => allowanceService.create(data),
    onSuccess: async () => {
      toast.success('Allowance created');
      await queryClient.invalidateQueries({ queryKey: ['allowances'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create allowance');
    },
  });

  const onSubmit = (data: FormInput) => {
    const parsed = schema.parse(data);
    mutation.mutate({
      tripId: parsed.tripId,
      type: parsed.type,
      amount: parsed.amount,
      notes: parsed.notes?.trim() ? parsed.notes : undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Allowance"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Select
          label="Trip"
          required
          error={errors.tripId?.message}
          options={(trips || []).map((t) => ({
            value: t.id,
            label: `${t.tripNumber} - ${t.driverName} - ${t.origin} → ${t.destination}`,
          }))}
          {...register('tripId')}
        />

        <Select
          label="Type"
          required
          error={errors.type?.message}
          options={[
            { value: AllowanceType.PER_DIEM, label: 'Per Diem' },
            { value: AllowanceType.MEAL, label: 'Meal' },
            { value: AllowanceType.NIGHT_OUT, label: 'Night Out' },
            { value: AllowanceType.OVERTIME, label: 'Overtime' },
            { value: AllowanceType.TURNBOY, label: 'Turnboy' },
            { value: AllowanceType.LOADING_OFFLOADING, label: 'Loading/Offloading' },
            { value: AllowanceType.LOCAL_TRIP, label: 'Local Trip' },
            { value: AllowanceType.UPCOUNTRY_TRIP, label: 'Upcountry Trip' },
            { value: AllowanceType.CROSS_BORDER, label: 'Cross Border' },
            { value: AllowanceType.REMOTE_AREA, label: 'Remote Area' },
            { value: AllowanceType.RISK_ZONE, label: 'Risk Zone' },
            { value: AllowanceType.COMMUNICATION, label: 'Communication' },
            { value: AllowanceType.PARKING, label: 'Parking' },
            { value: AllowanceType.TOLL_FEES, label: 'Toll Fees' },
            { value: AllowanceType.WEIGHBRIDGE, label: 'Weighbridge' },
            { value: AllowanceType.BORDER_PROCESSING, label: 'Border Processing' },
            { value: AllowanceType.REFRIGERATED_CARGO, label: 'Refrigerated Cargo' },
            { value: AllowanceType.HAZARDOUS_MATERIAL, label: 'Hazardous Material' },
            { value: AllowanceType.OVERSIZED_LOAD, label: 'Oversized Load' },
            { value: AllowanceType.NIGHT_DRIVING, label: 'Night Driving' },
            { value: AllowanceType.ON_TIME_BONUS, label: 'On-Time Bonus' },
            { value: AllowanceType.FUEL_EFFICIENCY_BONUS, label: 'Fuel Efficiency Bonus' },
            { value: AllowanceType.NO_ACCIDENT_BONUS, label: 'No Accident Bonus' },
            { value: AllowanceType.CUSTOMER_SATISFACTION_BONUS, label: 'Customer Satisfaction Bonus' },
          ]}
          {...register('type')}
        />

        <Input
          label="Amount"
          type="number"
          required
          placeholder="e.g., 50000"
          error={errors.amount?.message}
          {...register('amount')}
        />

        <Input
          label="Notes"
          placeholder="e.g., Meal allowance"
          error={errors.notes?.message}
          {...register('notes')}
        />

        {isLoading && <div className="text-sm text-gray-600">Loading trips...</div>}

        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
};
